import { useState } from 'react';
import { Button, Card, Alert } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  useGetReportsBySecurityGroupQuery,
  useGetAvailableReportsForSecurityGroupQuery,
  useAssignReportsToSecurityGroupMutation
} from '../../services/portal';
import ReportSelectionModal from './ReportSelectionModal';
import LoadingState from './LoadingState';

/**
 * Displays and manages reports assigned to a security group.
 * Provides search, list view, and a modal to add/remove report access in bulk.
 * - securityGroupId {string|number}: ID of the security group whose reports are shown/managed.
 * - organizationId {string|number}: ID of the organization, used to load available reports.
 * @returns {JSX.Element} Card with reports table and a modal to manage assignments.
 */
const GroupReports = ({ securityGroupId, organizationId }) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: groupReports,
    isLoading: isLoadingReports,
    refetch: refetchReports
  } = useGetReportsBySecurityGroupQuery(securityGroupId);

  const {
    data: availableReports
  } = useGetAvailableReportsForSecurityGroupQuery({
    securityGroupId,
    organizationId
  });

  const [assignReports] = useAssignReportsToSecurityGroupMutation();

  /**
   * Initializes the report selection from the group's current reports and opens the modal.
   * @returns {void}
   */
  const handleManageReports = () => {
    setSelectedReports(groupReports?.reports?.map(report => report.ReportID) || []);
    setShowReportModal(true);
  };

  /**
   * Persists selected report IDs to the backend and refreshes the list.
   * @param {number[]} reportIds - Array of report IDs to assign to the group.
   * @returns {Promise<void>}
   */
  const handleSaveReports = async (reportIds) => {
    try {
      await assignReports({
        securityGroupId: parseInt(securityGroupId),
        report_ids: reportIds
      }).unwrap();

      refetchReports();
      setShowReportModal(false);
    } catch (error) {
      console.error('Failed to assign reports:', error);
    }
  };

  /**
   * Renders a badge for the report type.
   * @param {Object} rowData - Row data object for a report.
   * @param {string} rowData.ReportType - The type label of the report.
   * @returns {JSX.Element} A colored badge indicating report type.
   */
  const reportTypeTemplate = (rowData) => {
    return (
      <span className={`badge ${rowData.ReportType === 'Report' ? 'bg-primary' : 'bg-secondary'}`}>
        {rowData.ReportType}
      </span>
    );
  };

  /**
   * Renders the date the report was added to the group.
   * @param {Object} rowData - Row data object for a report.
   * @param {string|number|Date} rowData.AddedToGroupDate - Date the report was added.
   * @returns {JSX.Element} A span with a localized date string.
   */
  const reportDateTemplate = (rowData) => {
    return (
      <span className="text-muted">
        {new Date(rowData.AddedToGroupDate).toLocaleDateString()}
      </span>
    );
  };

  const reports = groupReports?.reports || [];
  const available = availableReports?.reports || [];

  /**
   * Handles changes to the search input used to filter visible reports.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event.
   * @returns {void}
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /**
   * Filters the group's reports by the current search term.
   * Matches on any string field within a report row.
   * @returns {Object[]} Filtered list of reports.
   */
  const filterReports = () => {
    if (!reports || !reports.length) return [];
    return reports.filter(report => {
      return Object.values(report).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    });
  };

  const filteredReports = filterReports();

  if (isLoadingReports) {
    return (
      <LoadingState 
        title="Group Reports" 
        icon="fas fa-chart-pie"
        message="Loading group reports..." 
      />
    );
  }

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Group Reports</h5>
          <Button
            variant="primary"
            size="sm"
            onClick={handleManageReports}
          >
            Manage Reports
          </Button>
        </Card.Header>
        <Card.Body>
          {reports.length === 0 ? (
            <Alert variant="info">
              <div className="text-center">
                <i className="fas fa-chart-pie fa-3x text-muted mb-3"></i>
                <h5>No Reports Assigned</h5>
                <p className="text-muted">
                  This security group doesn't have access to any reports yet. Click "Manage Reports" to grant access to reports.
                </p>
                <Button variant="primary" onClick={handleManageReports}>
                  Assign Reports
                </Button>
              </div>
            </Alert>
          ) : (
            <div className="datatable-custom">
              <div className="m-0 justify-content-end d-flex align-items-end p-3">
                <input
                  type="text"
                  className="m-0 col-2 card d-flex align-items-end p-2"
                  placeholder="Type to Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <span className="p-inputgroup-addon">
                  <i className="pi pi-search"></i>
                </span>
              </div>
              <DataTable
                value={filteredReports}
                removableSort
                tableClassName="table dataTable no-footer table-striped"
                emptyMessage="No reports found"
              >
                <Column
                  field="ReportName"
                  header="Report Name"
                  sortable
                />
                <Column
                  field="ReportCode"
                  header="Code"
                  sortable
                />
                <Column
                  field="ReportDescription"
                  header="Description"
                  sortable
                />
                <Column
                  field="WorkspaceName"
                  header="Workspace"
                  sortable
                />
                <Column
                  body={reportTypeTemplate}
                  header="Type"
                  style={{ width: '100px' }}
                />
                <Column
                  body={reportDateTemplate}
                  header="Date Added"
                  style={{ width: '120px' }}
                />
              </DataTable>
            </div>
          )}
        </Card.Body>
      </Card>

      <ReportSelectionModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        groupReports={reports}
        availableReports={available}
        selectedReports={selectedReports}
        onSelectionChange={setSelectedReports}
        onSave={handleSaveReports}
      />
    </div>
  );
};

export default GroupReports;
